"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Image from "@/components/Image";
import Modal from "@/app/components/Modal";
// import { DeviceType, getDeviceTypeByUA } from "@/app/utils";

// 定义图片仓库地址常量
const IMAGE_URL = "/";

const Product = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const section1Ref = useRef(null); // 添加第一部分的引用
  const section2Ref = useRef<HTMLDivElement>(null); // 第二部分的引用
  const section3Ref = useRef<HTMLDivElement>(null); // 第三部分的引用
  const timeoutRefs = useRef<{ [key: string]: NodeJS.Timeout }>({}); // 使用 useRef 存储定时器
  const [activeSection, setActiveSection] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [imagePosition, setImagePosition] = useState<
    "initial" | "fixed" | "end"
  >("initial");
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [deviceType, setDeviceType] = useState<string>("other");

  // 在组件挂载时获取设备类型
  useEffect(() => {
    import("current-device").then((module) => {
      const deviceType = module.default.type;
      setDeviceType(deviceType);
      if (deviceType === "desktop") {
        setDeviceType("pc");
      }
      if (deviceType === "unknown") {
        setDeviceType("other");
      }
    });
  }, []);

  // 检测屏幕宽度变化
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(document.documentElement.clientWidth <= 768);
    };

    // 初始检查
    checkIfMobile();

    // 监听窗口大小变化
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // 监听滚动效果，实现图片滚动视差（仅在非移动端）
  useEffect(() => {
    // 如果是移动端，不添加滚动事件监听器
    if (isMobile) return;

    const handleScroll = () => {
      if (!sectionRef.current || !imageRef.current) return;
      const sections = sectionRef.current.querySelectorAll(".section-item");
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const middleOfWindow = windowHeight / 2;
      const imageHeight = imageRef.current.offsetHeight;
      const sectionHeight = sectionRef.current.offsetHeight;
      // 检查每个部分的位置，判断哪个部分在视口中间
      sections.forEach((section, index) => {
        const sectionRect = section.getBoundingClientRect();
        const sectionMiddle = sectionRect.top + sectionRect.height / 2;

        // 判断部分是否在视口中间附近
        if (Math.abs(sectionMiddle - middleOfWindow) < 200) {
          setActiveSection(index);
        }
      });

      // 顶部固定点位置 (窗口高度的30%)
      const fixedTop = 150; //windowHeight * 0.3;

      // 1. 初始进入阶段 - 图片跟随左侧内容一起进入
      if (sectionRect.top > fixedTop) {
        setImagePosition("initial");
        // 图片相对于左侧内容的顶部位置保持不变
        imageRef.current.style.position = "relative";
        imageRef.current.style.top = "0";
        imageRef.current.style.transform = "translateY(0)";
      }
      // 2. 固定阶段 - 图片固定在顶部30%位置
      else if (sectionRect.bottom > fixedTop + imageHeight) {
        setImagePosition("fixed");
        imageRef.current.style.position = "sticky";
        imageRef.current.style.top = "150px"; //"30vh";
        imageRef.current.style.transform = "translateY(0)";
      }
      // 3. 结束阶段 - 当左侧内容底部接近图片底部时，图片随着左侧内容一起移出
      else {
        setImagePosition("end");
        // 计算图片应该移动的距离，使其底部与左侧内容底部对齐
        imageRef.current.style.position = "relative";
        imageRef.current.style.top = `${sectionHeight - imageHeight}px`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // 初始加载时调用一次

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeSection, isMobile]);

  // 工具函数：防抖+日志上报封装
  const debounceLog = useCallback(
    (
      actionName: string,
      params: { feature_name: string; user_access_port: string },
      pageName: string,
      wait = 3000,
    ) => {
      const key = `${actionName}_${params.feature_name}`; // 为每个不同的日志创建唯一键

      return () => {
        if (timeoutRefs.current[key]) {
          clearTimeout(timeoutRefs.current[key]);
        }

        timeoutRefs.current[key] = setTimeout(() => {
          if (window.Log?.action) {
            window.Log.action(actionName, params, pageName);
          }
        }, wait);
      };
    },
    [],
  );

  // 在 useEffect 中监听 activeSection 变化时上报日志
  useEffect(() => {
    const features = [
      { value: "AI智能作业管理" },
      { value: "全场景家校沟通" },
      { value: "正向激励关注孩子成长" },
    ];

    if (!isMobile && activeSection >= 0 && activeSection < features.length) {
      const logAction = debounceLog(
        "feature_show",
        {
          feature_name: features[activeSection].value,
          user_access_port: deviceType,
        },
        "HWOfficialMainPage",
      );
      logAction();
    }
  }, [activeSection, debounceLog, isMobile, deviceType]);

  // 移动端布局的内容
  const mobileContent = (
    <div className="w-full max-w-[688px] flex flex-col">
      {/* 第一部分 */}
      <div className="w-full max-w-[688px] flex pb-[56px]">
        {/* 左侧图标 */}
        <div className="w-full max-w-[44px] flex-shrink-0">
          <Image
            src="/icon1_light_04.png"
            alt="作业管理图标"
            width={44}
            height={46}
            className={`w-full h-auto object-contain [@media(max-width:660px)]:max-w-[22px]`}
          />
        </div>

        {/* 右侧内容 */}
        <div className="w-full max-w-[624px] flex flex-col gap-[20px]">
          {/* 上方图片 */}
          <div
            className="w-full max-w-[624px] cursor-pointer"
            onClick={() => setShowVideo(true)}
          >
            <Image
              src="/video_04.png"
              alt="作业管理展示"
              width={624}
              height={352}
              className="w-full object-contain rounded-lg"
              onLoad={debounceLog(
                "feature_show",
                {
                  feature_name: "AI智能作业管理",
                  user_access_port: deviceType,
                },
                "HWOfficialMainPage",
              )}
            />
          </div>

          {/* 下方文字内容 */}
          <div className="w-full max-w-[624px] flex flex-col gap-[10px]">
            {/* 标题 */}
            <div className="w-full max-w-[624px]  font-[600] [@media(max-width:660px)]:text-[20px] text-[24px] leading-[44px] tracking-[0px] text-[#212121]">
              多样化作业管理 + AI 精准学情分析
            </div>

            {/* 描述 */}
            <div className="w-full max-w-[624px]  font-[400] [@media(max-width:660px)]:text-[14px] text-[16px] leading-[28px] tracking-[0px] text-[#666666] ">
              课文同步/打卡（跟读背诵AI评测+听写AI批改）、古诗词背诵、智慧作业（原卷手写AI批改）、作文批改、口算、白板讲题、班级错题本等功能满足老师教学场景所需，学情分析简单高效，助力老师精准教学。
            </div>
          </div>
        </div>
      </div>

      {/* 第二部分 */}
      <div ref={section2Ref} className="w-full max-w-[688px] flex pb-[56px]">
        {/* 左侧图标 */}
        <div className="w-full max-w-[44px] flex-shrink-0">
          <Image
            src={IMAGE_URL + "icon2_light_04.png"}
            alt="家校沟通图标"
            width={44}
            height={46}
            className={`w-full h-auto object-contain [@media(max-width:660px)]:max-w-[22px]`}
          />
        </div>

        {/* 右侧内容 */}
        <div className="w-full max-w-[624px] flex flex-col gap-[20px]">
          {/* 上方图片 */}
          <div className="w-full max-w-[624px]">
            <Image
              src="/pic1_04.png"
              alt="蜜蜂家校家校沟通更顺畅：私密成绩、接龙、通知、打卡、调查填表、活动报名投票..."
              width={624}
              height={352}
              className="w-full object-contain rounded-lg"
              onLoad={debounceLog(
                "feature_show",
                {
                  feature_name: "全场景家校沟通",
                  user_access_port: deviceType,
                },
                "HWOfficialMainPage",
              )}
            />
          </div>

          {/* 下方文字内容 */}
          <div className="w-full max-w-[624px] flex flex-col gap-[10px]">
            {/* 标题 */}
            <div className="w-full max-w-[624px]  font-[600] [@media(max-width:660px)]:text-[20px] text-[24px] leading-[44px] tracking-[0px] text-[#212121]">
              全场景家校沟通
            </div>

            {/* 描述 */}
            <div className="w-full max-w-[624px]  font-[400] [@media(max-width:660px)]:text-[14px] text-[16px] leading-[28px] tracking-[0px] text-[#666666]">
              私密成绩、接龙、通知、打卡、调查、报名、投票；
              <br />
              一键一对一送达全部家长，支持实时查看家长的查看情况，一键提醒未查看家长，家校沟通更流畅。
              <br />
              请假管理在线发起，请假类型、时段灵活配置，
              <br />
              老师批复，家长签字，学生安全更放心。
              <br />
              支持老师一键导出考勤、请假数据，家校沟通有保障。
            </div>
          </div>
        </div>
      </div>

      {/* 第三部分 */}
      <div ref={section3Ref} className="w-full max-w-[688px] flex pb-[56px]">
        {/* 左侧图标 */}
        <div className="w-full max-w-[44px] flex-shrink-0">
          <Image
            src="/icon3_light_04.png"
            alt="成长激励图标"
            width={44}
            height={46}
            className={`w-full h-auto object-contain [@media(max-width:660px)]:max-w-[22px]`}
          />
        </div>

        {/* 右侧内容 */}
        <div className="w-full max-w-[624px] flex flex-col gap-[20px]">
          {/* 上方图片 */}
          <div className="w-full max-w-[624px]">
            <Image
              src="/pic2_04.png"
              alt="蜜蜂家校关注孩子成长，正向激励高效共育：成长记录、奖状、智能评语、小红花..."
              width={624}
              height={352}
              className="w-full object-contain rounded-lg"
              onLoad={debounceLog(
                "feature_show",
                {
                  feature_name: "正向激励关注孩子成长",
                  user_access_port: deviceType,
                },
                "HWOfficialMainPage",
              )}
            />
          </div>

          {/* 下方文字内容 */}
          <div className="w-full max-w-[624px] flex flex-col gap-[10px]">
            {/* 标题 */}
            <div className="w-full max-w-[624px]  font-[600] [@media(max-width:660px)]:text-[20px] text-[24px] leading-[44px] tracking-[0px] text-[#212121]">
              正向激励关注孩子成长
            </div>

            {/* 描述 */}
            <div className="w-full max-w-[624px]  font-[400] [@media(max-width:660px)]:text-[14px] text-[16px] leading-[28px] tracking-[0px] text-[#666666]">
              成长记录、奖状、智能评语、小红花，
              <br />
              实时记录孩子成长每一步，及时给予孩子表现正反馈，
              <br />
              量化孩子每一次作业、在校高光时刻......
              <br />
              激发学生学习积极性，实现高效的家校共育。
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // 桌面端布局的内容
  const DesktopContent = () => {
    // 使用函数组件，确保imageRef只在非移动端渲染时被使用
    return (
      <div className="w-full flex gap-[clamp(10px,5vw,56px)] pb-[80px]">
        {/* 左侧部分 - 三个垂直排列的部分 */}
        <div
          ref={sectionRef}
          className="flex flex-col w-full min-w-[260px] max-w-[666px] h-auto gap-[30px] justify-center"
        >
          {/* 第一部分 */}
          <div
            ref={section1Ref}
            className="section-item flex w-full max-w-[530px] min-w-[260px] min-h-[360px] bg-white"
          >
            <div className="flex gap-5">
              {/* 左侧图标 */}
              <div className="w-full max-w-[44px] h-[46px] flex-shrink-0">
                <Image
                  src={
                    activeSection === 0
                      ? "/icon1_light_04.png"
                      : "/icon1_nomal_04.png"
                  }
                  alt="作业管理图标"
                  width={44}
                  height={46}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* 右侧内容 */}
              <div className="flex flex-col gap-3">
                {/* 标题 */}
                <h3
                  className={`w-full   font-semibold text-[24px] leading-[44px] tracking-[0px] ${
                    activeSection === 0 ? "text-[#1DBA55]" : "text-[#333333]"
                  }`}
                >
                  多样化作业管理 + AI 精准学情分析
                </h3>

                {/* 描述 */}
                <p className="w-full  font-normal text-[16px] leading-[28px] tracking-[0px] text-[#666666]">
                  课文同步/打卡（跟读背诵AI评测+听写AI批改）、古诗词背诵、智慧作业（原卷手写AI批改）、作文批改、口算、白板讲题、班级错题本等功能满足老师教学场景所需，学情分析简单高效，助力老师精准教学。
                </p>
              </div>
            </div>
          </div>

          {/* 第二部分 */}
          <div
            ref={section2Ref}
            className="section-item flex w-full max-w-[530px] min-w-[260px] min-h-[444px] bg-white"
          >
            <div className="flex gap-5">
              {/* 左侧图标 */}
              <div className="w-full max-w-[44px] h-[46px] flex-shrink-0">
                <Image
                  src={
                    activeSection === 1
                      ? "/icon2_light_04.png"
                      : "/icon2_nomal_04.png"
                  }
                  alt="家校沟通图标"
                  width={44}
                  height={46}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* 右侧内容 */}
              <div className="w-full flex flex-col gap-3">
                {/* 标题 */}
                <h3
                  className={`w-full  font-semibold text-[24px] leading-[44px] tracking-[0px] ${
                    activeSection === 1 ? "text-[#1DBA55]" : "text-[#333333]"
                  }`}
                >
                  全场景家校沟通
                </h3>

                {/* 描述 */}
                <p className="w-full  font-normal text-[16px] leading-[28px] tracking-[0px] text-[#666666]">
                  私密成绩、接龙、通知、打卡、调查、报名、投票；
                  <br />
                  一键一对一送达全部家长，支持实时查看家长的查看情况，一键提醒未查看家长，家校沟通更流畅。
                  <br />
                  请假管理在线发起，请假类型、时段灵活配置，
                  <br />
                  老师批复，家长签字，学生安全更放心。
                  <br />
                  支持老师一键导出考勤、请假数据，家校沟通有保障。
                </p>
              </div>
            </div>
          </div>

          {/* 第三部分 */}
          <div
            ref={section3Ref}
            className="section-item flex w-full max-w-[530px] min-w-[260px] min-h-[360px] bg-white"
          >
            <div className="flex gap-5">
              {/* 左侧图标 */}
              <div className="w-[44px] h-[44px] flex-shrink-0">
                <Image
                  src={
                    activeSection === 2
                      ? "/icon3_light_04.png"
                      : "/icon3_nomal_04.png"
                  }
                  alt="成长激励图标"
                  width={44}
                  height={44}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* 右侧内容 */}
              <div className="flex flex-col gap-3">
                {/* 标题 */}
                <h3
                  className={`w-full  font-semibold text-[24px] leading-[44px] tracking-[0px] ${
                    activeSection === 2 ? "text-[#1DBA55]" : "text-[#333333]"
                  }`}
                >
                  正向激励关注孩子成长
                </h3>

                {/* 描述 */}
                <p className="w-full  font-normal text-[16px] leading-[28px] tracking-[0px] text-[#666666]">
                  成长记录、奖状、智能评语、小红花，
                  <br />
                  实时记录孩子成长每一步，及时给予孩子表现正反馈，
                  <br />
                  量化孩子每一次作业、在校高光时刻......
                  <br />
                  激发学生学习积极性，实现高效的家校共育。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧部分 - 图片 */}
        <div
          ref={imageRef}
          className={`w-full min-w-[500px] max-w-[638px] max-h-[360px] rounded-lg gap-[30px] transition-transform duration-300`}
          style={{
            position: imagePosition === "fixed" ? "sticky" : "relative",
            top: imagePosition === "fixed" ? "30%" : "0",
          }}
        >
          <div
            className={`relative w-full h-full ${activeSection === 0 ? "cursor-pointer" : "cursor-default"}`}
          >
            {/* 使用三个图片元素，根据activeSection控制显示/隐藏 */}
            <div
              className={`absolute inset-0 ${activeSection === 0 ? "opacity-100" : "opacity-0"} `}
            >
              <Image
                src="/video_04.png"
                alt="视频展示"
                width={638}
                height={360}
                className="w-full h-atuo object-contain"
              />
            </div>

            <div
              className={`absolute inset-0 ${activeSection === 1 ? "opacity-100" : "opacity-0"}`}
            >
              <Image
                src={"/pic1_04.png"}
                alt="蜜蜂家校家校沟通更顺畅：私密成绩、接龙、通知、打卡、调查填表、活动报名投票..."
                width={638}
                height={360}
                className="w-full h-full object-contain "
              />
            </div>

            <div
              className={`absolute inset-0 ${activeSection === 2 ? "opacity-100" : "opacity-0"}`}
              onClick={() => setShowVideo(true && activeSection === 0)}
            >
              <Image
                src="/pic2_04.png"
                alt="蜜蜂家校关注孩子成长，正向激励高效共育：成长记录、奖状、智能评语、小红花..."
                width={638}
                height={360}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="flex w-full px-[80px] [@media(min-width:769px)_and_(max-width:1080px)]:px-[40px] [@media(max-width:768px)]:px-[20px] pb-[10px] bg-white mx-auto">
      {/* 主体部分 */}
      <div className="flex flex-col w-full max-w-[1360px] mx-auto h-auto">
        {/* 上面部分 - 标题区域 */}
        <div className="flex flex-col w-full max-w-[688px] min-h-[100px] mx-auto items-center justify-center pb-[80px] pt-[80px] [@media(max-width:660px)]:pt-[60px] gap-[20px]">
          {/* 图标 - 在小屏幕上隐藏 */}
          <div className="hidden [@media(max-width:660px)]:flex w-full max-w-[58px] h-auto border border-[#FFFFFF1A]">
            <Image src="/icon4_04.png" alt="功能图标" width={58} height={56} />
          </div>
          {/* 第一行 - 标题文字和图标并排 */}
          <div className="flex w-full flex-row items-center justify-center">
            {/* 标题文字 */}
            <h2 className="h-auto font-semibold text-[46px] [@media(min-width:415px)_and_(max-width:660px)]:text-[40px] [@media(max-width:660px)]:text-[clamp(2rem,9vw,2.5rem)] leading-[52px] tracking-[0px] text-[#333333] text-center">
              超20+功能场景覆盖
            </h2>

            {/* 图标 - 在小屏幕上隐藏 */}
            <div className="[@media(max-width:660px)]:hidden w-full max-w-[58px] h-auto border border-[#FFFFFF1A]">
              <Image
                src="/icon4_04.png"
                alt="功能图标"
                width={58}
                height={56}
              />
            </div>
          </div>

          {/* 第二行 - 描述文字 */}
          <p className="w-full h-auto  font-normal text-[16px] [@media(max-width:660px)]:text-[14px] sm:text-[18px] leading-[28px] tracking-[1px] sm:tracking-[2px] text-center text-[#666666] px-4 sm:px-0">
            蜜蜂AI功能全面，扫描全能王出品，助力老师有效开展“人工智能+教与学”
          </p>
        </div>

        {/* 下面部分 - 内容区域 (根据屏幕尺寸切换布局) */}
        {isMobile ? mobileContent : <DesktopContent />}
      </div>
      <Modal show={showVideo} type="3" onClose={() => setShowVideo(false)} />
    </section>
  );
};

export default Product;
